import React, { useState, useContext } from 'react'
import ReactDOM from 'react-dom'
import { useMutation } from '@apollo/client'
import { DialogContext } from '../context/DialogContext.js'
import UserContext from '../context/UserContext.js'
import languages, { getLanguageAcronym } from '../languages.js'
import { ADD_MEETING_WITH_CONTENT_MUTATION } from '../query/gqlQueries.js'
import styles from './AddProjectDialog.module.scss'
import CloseIcon from '@mui/icons-material/Close'
import readXlsxFile from 'read-excel-file'
import { streamOllamaGenerate } from '../ollama.js'

// Vision models first (they read images incl. non-Latin/mixed script and also
// handle the text path). qwen2.5vl:32b is the default — the jump from 7B → 32B
// is where mixed-script lines (e.g. Korean with an embedded "(K-pop)") stop
// getting flipped to English. gemma3:27b is multimodal too and a strong
// already-installed fallback; keep qwen2.5vl:7b as the fast option.
const MODELS = [
  'qwen2.5vl:32b',           // best OCR for CJK / mixed-script (default)
  'gemma3:27b',              // multimodal, strong fallback
  'qwen2.5vl:7b',            // fast vision for simple pages
  'llama3.2-vision:latest',
  'qwen2.5:14b',             // text-only: tasks / translation
  'llama3.1:8b',
  'qwen2.5:7b',
  'qwen2.5:3b',
]

// A tutor often writes a whole paradigm on one line — every form of a
// construction, space-separated. This is language-agnostic: it looks purely at
// the SHAPE of the line, not at any specific words. It fires on a short particle
// that repeats at a constant stride, whether leading or trailing:
//   RU  "у меня у тебя у вас у них …"      (leading "у")
//   FR  "à moi à toi à lui à nous"         (leading "à")
//   ES  "de mí de ti de él"                (leading "de")
// It CANNOT catch paradigms with no repeated token ("мой твой наш ваш") or
// suffix languages where the marker is glued on with no space (Korean/Turkish):
// those are left to the model + prompt. Returns null for ordinary lines so they
// pass through untouched.
//
// Splits `list` into groups that each begin at one of `positions` (a repeated
// token's indices) and tile the whole array at a constant stride.
function tileByPositions(list, positions) {
  return positions.map((start, k) => {
    const end = k + 1 < positions.length ? positions[k + 1] : list.length
    return list.slice(start, end).join(' ')
  })
}

// Indices where a short repeated marker sits, if the line is a paradigm keyed on
// `edge` ('lead' = marker starts each group, 'trail' = marker ends each group).
function paradigmPositions(tokens, edge) {
  const anchor = (edge === 'lead' ? tokens[0] : tokens[tokens.length - 1]).toLowerCase()
  if (anchor.length > 3) return null                 // particle, not a content word
  const at = tokens.reduce((acc, t, i) => (t.toLowerCase() === anchor ? [...acc, i] : acc), [])
  if (at.length < 3) return null
  const stride = at[1] - at[0]
  if (stride < 1 || stride > 4) return null
  if (!at.every((p, k) => k === 0 || p - at[k - 1] === stride)) return null
  if (edge === 'lead') {
    if (at[0] !== 0 || tokens.length - at[at.length - 1] !== stride) return null
    return at
  }
  // Trailing marker: convert to group *starts* so the groups still tile forwards.
  if (at[at.length - 1] !== tokens.length - 1) return null
  const starts = at.map((p) => p - (stride - 1))
  if (starts[0] !== 0) return null
  return starts
}

// Break one line into paradigm members, or return null. Two shapes, tried in
// order — both language-agnostic:
//   1. Explicit delimiters (comma / semicolon / newline / slash). Catches a
//      conjugation with a DIFFERENT word each person, which has no repeated
//      token to key on: "eu falo, tu falas, ele fala, nós falamos".
//   2. A short particle repeating at a constant stride (leading or trailing),
//      for delimiter-free lists: "у меня у тебя у вас …", "à moi à toi à lui".
function segmentForms(str) {
  const s = String(str || '').trim()
  if (!s) return null

  // 1. Delimited list. Require ≥3 pieces, each SHORT (a form, not a clause), so
  // an ordinary sentence with a comma or two is never shredded.
  const byDelim = s.split(/\s*[;,\n]\s*|\s+\/\s+/).map((x) => x.trim()).filter(Boolean)
  if (
    byDelim.length >= 3 &&
    byDelim.every((p) => p.split(/\s+/).length <= 4 && !/[?!]/.test(p))  // short forms, not clauses
  ) return byDelim

  // 2. Repeated-particle list.
  const tokens = s.split(/\s+/).filter(Boolean)
  if (tokens.length >= 4) {
    const pos = paradigmPositions(tokens, 'lead') || paradigmPositions(tokens, 'trail')
    if (pos) return tileByPositions(tokens, pos)
  }
  return null
}

function splitParadigm(source, target) {
  const pieces = segmentForms(source)
  if (!pieces) return null
  // Split the translation the same way; only pair it up if the counts match,
  // otherwise leave targets blank rather than mis-aligning them.
  let targetPieces = segmentForms(target)
  if (!targetPieces || targetPieces.length !== pieces.length) targetPieces = null
  return pieces.map((p, i) => ({ source: p, target: targetPieces ? targetPieces[i] : '' }))
}

// Backstop for a vocabulary list the OCR joined into one line: N words with no
// sentence punctuation whose English translation has the SAME number of words
// almost never happens for a real sentence (translations change length), but is
// exactly what a word-list-paired-to-word-list looks like. Split it 1:1 into
// words. Returns null unless the shape is clearly a parallel list.
function splitParallelWordList(source, target) {
  const s = String(source || '').trim()
  const t = String(target || '').trim()
  if (/[.,!?…;:"'«»()]/.test(s)) return null            // punctuation → a real sentence
  if (/\d/.test(s)) return null                          // numbers → a sentence about quantities
  const sTok = s.split(/\s+/).filter(Boolean)
  const tTok = t.split(/\s+/).filter(Boolean)
  if (sTok.length < 6) return null                       // too short to be sure
  if (tTok.length !== sTok.length) return null           // must pair 1:1
  // A vocab list is content words; a real sentence has short function words
  // (de, o, a, da, до…). If any source token is < 3 letters, treat as a sentence.
  const letters = (w) => (w.match(/\p{L}/gu) || []).length
  if (!sTok.every((w) => letters(w) >= 3)) return null
  return sTok.map((w, i) => ({ source: w, target: tTok[i] }))
}

// A line is a sentence if it contains a space OR ends with sentence
// punctuation. The punctuation check is what makes this work for Korean and
// Japanese, where a full sentence can be one space-free token ("아버지예요.")
// that the space-only rule would wrongly file as vocabulary.
const looksLikeSentence = (text) =>
  /\s/.test(String(text)) || /[.!?…。？！¿¡]$/.test(String(text).trim())

// A bare filename or a long hex/hash token — chat exports paste these in when
// someone shares an image, and they must never become "vocabulary".
const looksLikeFilename = (s) => {
  const t = String(s || '').trim()
  if (/^["']?\S+\.(jpe?g|png|gif|webp|heic|bmp|tiff?|svg|mp4|mov|m4v|pdf|docx?|xlsx?|pptx?|zip)["']?$/i.test(t)) return true
  if (/^[0-9a-f]{16,}$/i.test(t)) return true                 // hash-like id
  if (/^[0-9a-f]{8,}\.[a-z0-9]{2,4}$/i.test(t)) return true   // hash.ext
  return false
}

// Strip the chrome out of a pasted/exported lesson chat (Preply, Zoom, Telegram):
// bullet markers, bare "10:26" timestamps, Zoom "…From Name to Everyone:" headers,
// filename/hash lines, and the sender-name line that sits above a timestamp. Real
// vocab lines survive one per line — so a paradigm across several lines stays so.
function cleanChatText(raw) {
  const lines = String(raw || '').split(/\r\n|\r|\n/)
  const strip = (l) => l.replace(/^\s*[*•·•-]\s*/, '').trim()
  const isBlank = (l) => strip(l) === ''
  const isTimestamp = (l) => /^\s*\d{1,2}:\d{2}\s*(am|pm)?\s*$/i.test(strip(l))
  // Zoom/Teams export header, e.g. "2026-07-27 12:11:07 From Raphael Maciel to Everyone:"
  const isChatHeader = (l) =>
    /\bFrom\b.+\bto\b.+:\s*$/i.test(strip(l)) ||
    /^\s*\d{4}-\d{2}-\d{2}[ T]\d{1,2}:\d{2}/.test(l)

  const out = []
  for (let i = 0; i < lines.length; i++) {
    const l = strip(lines[i])
    if (l === '' || isTimestamp(lines[i]) || isChatHeader(lines[i]) || looksLikeFilename(l)) continue
    // A sender-name line: the next non-blank line is a timestamp. Drop it.
    let j = i + 1
    while (j < lines.length && isBlank(lines[j])) j++
    if (j < lines.length && isTimestamp(lines[j])) continue
    out.push(l)
  }
  return out.join('\n')
}

// Pull clean, newline-preserving text out of a rich paste and throw the images
// away. Prefers the HTML flavour (so block breaks become real newlines even when
// the plain-text flavour is flattened), falls back to text/plain.
function extractPastedText(clipboardData) {
  const html = clipboardData?.getData?.('text/html')
  if (html) {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    doc.querySelectorAll('img, svg, style, script').forEach((n) => n.remove())
    doc.querySelectorAll('br').forEach((br) => br.replaceWith(doc.createTextNode('\n')))
    doc.querySelectorAll('p, div, li, tr, h1, h2, h3').forEach((el) => el.appendChild(doc.createTextNode('\n')))
    return (doc.body.textContent || '').replace(/ /g, ' ')
  }
  return clipboardData?.getData?.('text/plain') || ''
}

// Parse a model response into JSON even when it's fenced or has stray prose
// around it — salvage the {...} span rather than giving up (which showed 0).
function parseLooseJson(raw) {
  let s = String(raw || '').replace(/```json/gi, '').replace(/```/g, '').trim()
  try { return JSON.parse(s) } catch { /* try to salvage */ }
  const a = s.indexOf('{'); const b = s.lastIndexOf('}')
  if (a >= 0 && b > a) { try { return JSON.parse(s.slice(a, b + 1)) } catch { /* give up */ } }
  return null
}

const loadImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file)
  const img = new Image()
  img.onload = () => resolve(img)
  img.onerror = (e) => { URL.revokeObjectURL(url); reject(e) }
  img.src = url
})

// Slice a tall page-image into overlapping, legible tiles → array of base64
// JPEGs. Vision models squash a single 500×4000 strip until the text is
// unreadable (which is what made it ramble 15k chars and extract nothing);
// ~page-sized tiles OCR far better. Short images pass through as one tile.
async function imageToTiles(file) {
  let img
  try { img = await loadImage(file) } catch { return [] }
  const url = img.src
  const w = img.naturalWidth || img.width
  const h = img.naturalHeight || img.height
  const maxW = 1200
  const scale = w > maxW ? maxW / w : 1
  const sw = Math.round(w * scale)
  const enc = (c) => c.toDataURL('image/jpeg', 0.85).split(',')[1]
  const draw = (srcY, srcH) => {
    const c = document.createElement('canvas')
    c.width = sw
    c.height = Math.round(srcH * scale)
    c.getContext('2d').drawImage(img, 0, srcY, w, srcH, 0, 0, sw, c.height)
    return enc(c)
  }
  const TILE = 1100  // rendered tile height
  const OVERLAP = 150 // so a line split across a boundary survives in one tile
  try {
    if (h * scale <= TILE * 1.4) return [draw(0, h)]
    const tiles = []
    const stepSrc = (TILE - OVERLAP) / scale
    const tileSrcH = TILE / scale
    for (let y = 0; y < h; y += stepSrc) {
      const srcH = Math.min(tileSrcH, h - y)
      tiles.push(draw(y, srcH))
      if (y + srcH >= h) break
    }
    return tiles
  } finally {
    URL.revokeObjectURL(url)
  }
}

export default function AddMeetingDocumentDialog() {
  const { dialogStates, closeDialog } = useContext(DialogContext)
  const { username } = useContext(UserContext)

  const [files, setFiles] = useState([])
  const [pastedText, setPastedText] = useState('')
  const [showPaste, setShowPaste] = useState(false)
  const [status, setStatus] = useState('idle') // idle, analyzing, reviewing, saving, completed, error
  const [errorMessage, setErrorMessage] = useState('')
  const [model, setModel] = useState('qwen2.5vl:32b')
  const [targetLanguage, setTargetLanguage] = useState('Portuguese')
  const [meetingTitle, setMeetingTitle] = useState('')
  const [meetingType, setMeetingType] = useState('Language Class')
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().split('T')[0])
  const [previewData, setPreviewData] = useState(null)
  const [progressMsg, setProgressMsg] = useState('')

  const [addMeetingWithContent] = useMutation(ADD_MEETING_WITH_CONTENT_MUTATION)

  const isOpen = dialogStates['dialogAddMeetingDocument']

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFiles(Array.from(e.target.files))
    }
  }

  const handleClose = () => {
    closeDialog('dialogAddMeetingDocument')
    setFiles([])
    setPastedText('')
    setShowPaste(false)
    setStatus('idle')
    setPreviewData(null)
    setErrorMessage('')
    setMeetingTitle('')
    setMeetingType('Language Class')
    setMeetingDate(new Date().toISOString().split('T')[0])
    setProgressMsg('')
  }

  // Intercept the paste so images are dropped and line breaks are preserved,
  // then insert the cleaned text at the cursor.
  const handlePaste = (e) => {
    const text = extractPastedText(e.clipboardData)
    if (!text) return // nothing useful; let the browser handle it
    e.preventDefault()
    const el = e.target
    const start = el.selectionStart ?? pastedText.length
    const end = el.selectionEnd ?? pastedText.length
    const merged = pastedText.slice(0, start) + text + pastedText.slice(end)
    setPastedText(merged.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n'))
  }

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result.split(',')[1])
      reader.onerror = (error) => reject(error)
    })

  const fileToText = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsText(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    })

  const xlsxToText = async (file) => {
    const rows = await readXlsxFile(file)
    return rows
      .map((row) => row.map((cell) => (cell == null ? '' : String(cell).trim())).filter(Boolean).join(' | '))
      .filter((line) => line.length > 0)
      .join('\n')
  }

  const handleAnalyze = async () => {
    if (files.length === 0 && !pastedText.trim()) return
    setStatus('analyzing')
    setErrorMessage('')
    setProgressMsg('Preparing files...')

    // Resolve the ISO-3 code for the language the user selected ("Korean" → "kor").
    // Fall back to 'eng' only if the lookup truly fails, but warn — the dropdown
    // should always produce a recognised name.
    const srcLang = getLanguageAcronym(targetLanguage) || 'eng'
    if (srcLang === 'eng' && targetLanguage !== 'English') {
      console.warn(`Could not resolve language code for "${targetLanguage}" — defaulting to 'eng'`)
    }

    const totalStartTime = performance.now()

    try {
      const imageTiles = []   // { label, b64 } — tall pages sliced into legible tiles
      let textContents = ''

      for (const file of files) {
        if (file.type.startsWith('image/')) {
          const tiles = await imageToTiles(file)
          tiles.forEach((b64, ti) => imageTiles.push({
            label: tiles.length > 1 ? `${file.name} (part ${ti + 1}/${tiles.length})` : file.name,
            b64,
          }))
        } else if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
          // Same cleanup the paste box uses — strips Zoom headers, timestamps,
          // and pasted image filenames before the model ever sees them.
          const txt = cleanChatText(await fileToText(file))
          textContents += `\n--- Chat Log ---\n${txt}\n`
        } else if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
          const txt = await xlsxToText(file)
          textContents += `\n--- Spreadsheet (${file.name}) ---\n${txt}\n`
        }
      }

      // Pasted lesson chat — cleaned of names/timestamps/bullets, newlines kept.
      const cleanedPaste = cleanChatText(pastedText)
      if (cleanedPaste.trim()) {
        textContents += `\n--- Lesson Chat (pasted) ---\n${cleanedPaste}\n`
      }

      const prompt = `
You are an expert language teacher and meeting analyzer.
Analyze the provided material. The target language being learned or spoken is ${targetLanguage}.

CRITICAL INSTRUCTIONS:
1. Extract ALL vocabulary words and full sentences you see in the images or text.
2. Do NOT break sentences down into individual words. Only extract a word if it stands alone as vocabulary.
3. If an English translation is not visible, YOU MUST generate the English translation yourself. Never leave "targetText" or "translation" blank.
4. If a single line lists several parallel forms of ONE construction — a grammar paradigm (all the possessive/pronoun forms, a full verb conjugation, a set of case endings) in ANY language — output EACH form as its OWN separate entry with its OWN precise English translation. NEVER merge a paradigm into one run-on entry. This applies whatever the target language is: e.g. ${targetLanguage} paradigms should each be split form-by-form, not left as one long line.
5. If the material is a VERTICAL LIST or column of single words (a vocabulary list — each word on its own line, often with a tick/check mark beside it), output EACH word as its OWN separate entry in "words". NEVER join a column of separate words into one long sentence. Ignore tick marks, checkmarks, arrows and other decorations.
6. Ignore any image file names, hashes, URLs, timestamps, and speaker names — they are NOT vocabulary.

Return ONLY a valid JSON object. Do not include markdown code blocks or any other text outside the JSON.

{
  "sentences": [
    { "sourceText": "Text in ${targetLanguage}", "targetText": "English translation" }
  ],
  "words": [
    { "word": "Text in ${targetLanguage}", "translation": "English translation" }
  ]
}
      `.trim()

      const serverUrl = process.env.REACT_APP_OLLAMA_URI || 'http://localhost:11434'

      let allSentences = []
      let allWords = []
      let tilesRead = 0 // tiles that returned usable JSON

      // OCR each tile. num_predict caps the runaway (an unreadable strip made it
      // spew 15k chars and parse to nothing); loose parsing salvages slightly
      // malformed or fenced JSON instead of silently dropping the whole tile.
      for (let i = 0; i < imageTiles.length; i++) {
        const tile = imageTiles[i]
        setProgressMsg(`Reading ${tile.label} (${i + 1}/${imageTiles.length})…`)

        const startTime = performance.now()
        const rawResponse = await streamOllamaGenerate({
          serverUrl,
          apiKey: process.env.REACT_APP_OLLAMA_KEY,
          body: {
            model,
            prompt,
            format: 'json',
            options: { temperature: 0.1, num_predict: 1600 },
            images: [tile.b64]
          },
          onProgress: (acc) => {
            setProgressMsg(`Reading ${tile.label} (${i + 1}/${imageTiles.length})… ${acc.length} chars`)
          }
        })
        const elapsed = ((performance.now() - startTime) / 1000).toFixed(2)
        console.log(`[Timer] ${tile.label} took ${elapsed}s, ${rawResponse.length} chars`)

        const parsed = parseLooseJson(rawResponse)
        if (parsed) {
          tilesRead++
          if (parsed.sentences) allSentences.push(...parsed.sentences)
          if (parsed.words) allWords.push(...parsed.words)
        } else {
          console.warn(`Unparseable OCR output for ${tile.label}:`, rawResponse.slice(0, 300))
        }
      }

      // Text-only input (pasted lesson / .txt / .xlsx) doesn't need a vision
      // model — and the heavy 32B vision model over the tunnel is exactly what
      // drops mid-stream. Fall back to a fast text model when there's no image
      // to OCR, so a plain paste actually completes.
      const effectiveModel = (imageTiles.length === 0 && /vl|vision/i.test(model)) ? 'qwen2.5:14b' : model

      // If a chat log was provided, process it separately
      if (textContents) {
        setProgressMsg(effectiveModel !== model ? `No images — using ${effectiveModel} for text…` : 'Analyzing text...')

        const startTime = performance.now()
        const rawResponse = await streamOllamaGenerate({
          serverUrl,
          apiKey: process.env.REACT_APP_OLLAMA_KEY,
          body: {
            model: effectiveModel,
            prompt: prompt + `\nHere is the text context:\n${textContents}`,
            format: 'json',
            options: { temperature: 0.1, num_predict: 3000 }
          },
          onProgress: (acc) => {
            setProgressMsg(`Analyzing chat log text… (${acc.length} chars streamed)`)
          }
        })
        const elapsed = ((performance.now() - startTime) / 1000).toFixed(2)
        console.log(`[Timer] Chat log text analysis took ${elapsed} seconds`)

        const parsed = parseLooseJson(rawResponse)
        if (parsed) {
          if (parsed.sentences) allSentences.push(...parsed.sentences)
          if (parsed.words) allWords.push(...parsed.words)
        } else {
          console.warn('Failed to parse JSON for text', rawResponse.slice(0, 300))
        }
      }

      // --- PROGRAMMATIC CLEANUP & ENFORCEMENT ---
      let finalSentences = []
      let finalWords = []
      
      // Combine all raw items to process them identically
      let rawItems = [
        ...allSentences.map(s => ({ source: s.sourceText, target: s.targetText })), 
        ...allWords.map(w => ({ source: w.word, target: w.translation }))
      ]

      rawItems.forEach(item => {
        if (!item.source) return
        // Drop image filenames / hashes that leaked in from a chat export.
        if (looksLikeFilename(item.source)) return

        let source = item.source.trim()
        let target = item.target?.trim() || ''

        // 1. Clean parentheses for translations e.g., "você bebe (you drink)"
        const parenMatch = source.match(/^(.*?)\s*\((.*?)\)$/)
        if (parenMatch) {
          source = parenMatch[1].trim()
          target = parenMatch[2].trim() || target // prioritize english inside parentheses
        }

        // 1a. A vocabulary list the OCR merged into one line → split into words.
        const wordList = splitParallelWordList(source, target)
        if (wordList) {
          wordList.forEach(({ source: p, target: t }) => {
            if (p && !looksLikeFilename(p)) finalWords.push({ word: p, translation: t, lang: srcLang })
          })
          return
        }

        // 1b. Expand a paradigm line ("у меня у тебя у вас …") into its members,
        // each with its own translation, before anything else touches it.
        const paradigm = splitParadigm(source, target)
        if (paradigm) {
          paradigm.forEach(({ source: p, target: t }) => {
            if (!p) return
            const isSentence = looksLikeSentence(p)
            if (isSentence) finalSentences.push({ sourceText: p, targetText: t, sourceLang: srcLang, targetLang: 'eng' })
            else finalWords.push({ word: p, translation: t, lang: srcLang })
          })
          return
        }

        // 2. Split arrows or dashes for variations/translations e.g., "copo -> copinho" or "olmak — to be"
        const hasArrow = source.includes('->')
        const hasEmDash = source.includes(' — ')
        const hasEnDash = source.includes(' – ')
        const hasHyphen = source.includes(' - ')

        if (hasArrow || hasEmDash || hasEnDash || hasHyphen) {
           let separator = ' - '
           if (hasArrow) separator = '->'
           else if (hasEmDash) separator = ' — '
           else if (hasEnDash) separator = ' – '

           const parts = source.split(separator).map(p => p.trim())
           const tParts = target.includes(separator) ? target.split(separator).map(p => p.trim()) : [target, target]
           
           parts.forEach((p, idx) => {
             if (!p) return

             // If the split part is actually the translation (e.g. English) and we already have a target, skip it
             // Or if it's the second part and we didn't split the target, use the second part AS the target
             let finalTarget = tParts[idx] || target
             if (parts.length === 2 && idx === 0) {
               finalTarget = parts[1] // The right side of the dash is the translation
             } else if (parts.length === 2 && idx === 1) {
               return // Skip the right side since we used it as the target for the left side
             }

             const isSentence = looksLikeSentence(p)
             if (isSentence) finalSentences.push({ sourceText: p, targetText: finalTarget, sourceLang: srcLang, targetLang: 'eng' })
             else finalWords.push({ word: p, translation: finalTarget, lang: srcLang })
           })
           return
        }

        // 3. Strict Categorization: Single word vs Sentence
        const isSentence = looksLikeSentence(source)
        if (isSentence) {
          finalSentences.push({ sourceText: source, targetText: target, sourceLang: srcLang, targetLang: 'eng' })
        } else {
          finalWords.push({ word: source, translation: target, lang: srcLang })
        }
      })

      // 4. Deduplicate. Key on letters/numbers only (lowercased) so "do", "do ",
      // "do," and "Do" all collapse to one — the old toLowerCase-only key let a
      // model repeating a word with tiny trailing differences slip through as
      // dozens of "duplicates". Empty-key junk (pure punctuation) is dropped.
      // NFC first so "está" (e + combining accent) and "está" (precomposed é)
      // hash the same; then lowercase and drop punctuation/whitespace.
      const dedupKey = (s) => String(s || '').normalize('NFC').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()
      const uniqueSentences = Array.from(
        new Map(finalSentences.filter(i => dedupKey(i.sourceText)).map(item => [dedupKey(item.sourceText), item])).values()
      )
      const uniqueWords = Array.from(
        new Map(finalWords.filter(i => dedupKey(i.word)).map(item => [dedupKey(item.word), item])).values()
      )

      // Nothing came back — surface a real message rather than an empty preview.
      if (uniqueSentences.length === 0 && uniqueWords.length === 0) {
        setStatus('error')
        setErrorMessage(
          imageTiles.length > 0 && tilesRead === 0
            ? 'The vision model couldn’t read that image. A full lesson page exported as one image is hard to OCR — try a stronger vision model, or paste the lesson text directly.'
            : 'No vocabulary or sentences could be extracted. Try the “Paste lesson text directly” box, or a different model.'
        )
        return
      }

      // 5. Generate Tasks if Language Class
      let finalTasks = []
      if (meetingType === 'Language Class' && (uniqueWords.length > 0 || uniqueSentences.length > 0)) {
        setProgressMsg('Generating homework tasks...')
        
        // Chunk words to avoid overloading a single task
        const chunkedWords = []
        for (let i = 0; i < uniqueWords.length; i += 15) {
          chunkedWords.push(uniqueWords.slice(i, i + 15).map(w => w.word).join(', '))
        }
        
        let wordPromptStr = ''
        let taskInstruction = ''
        let jsonExampleTasks = []
        
        if (chunkedWords.length > 0) {
          if (chunkedWords.length === 1) {
            wordPromptStr = `Vocabulary Words: ${chunkedWords[0]}`
            taskInstruction = `Generate 2 to 3 practical homework tasks using the Vocabulary Words and Sentences.`
            jsonExampleTasks.push(`    { "title": "Practice verbs", "type": "Homework", "description": "Write sentences using the vocabulary...", "priority": "Medium" }`)
            jsonExampleTasks.push(`    { "title": "Fill in the blanks", "type": "Homework", "description": "Use the words to complete...", "priority": "Low" }`)
          } else {
            wordPromptStr = chunkedWords.map((chunk, idx) => `Word Batch ${idx + 1}: ${chunk}`).join('\n')
            taskInstruction = `CRITICAL: There are ${chunkedWords.length} Word Batches. YOU MUST GENERATE EXACTLY ${chunkedWords.length} vocabulary tasks (one for each Word Batch). Plus, generate 1 extra task for the Sentences if they exist. You must return exactly ${chunkedWords.length + (uniqueSentences.length > 0 ? 1 : 0)} tasks total. Do not skip any Word Batch. Do not mix words from different batches into a single task.`
            
            for (let i = 0; i < chunkedWords.length; i++) {
              jsonExampleTasks.push(`    { "title": "Practice verbs (Part ${i + 1})", "type": "Homework", "description": "Write sentences using verbs from Word Batch ${i + 1}...", "priority": "Medium" }`)
            }
          }
        }
        
        if (uniqueSentences.length > 0) {
           jsonExampleTasks.push(`    { "title": "Practice Sentences", "type": "Homework", "description": "Practice the provided sentences...", "priority": "Medium" }`)
        }
        
        const jsonExample = `{\n  "tasks": [\n${jsonExampleTasks.join(',\\n')}\n  ]\n}`
        
        const taskPrompt = `
You are an expert language teacher. Based on the following vocabulary and sentences learned in class today, generate practical homework tasks for the student.

${taskInstruction}
Each task should have a short generic action title, be of type "Homework", and have a detailed description using the specific words/sentences for that task.

${wordPromptStr}
Sentences: ${uniqueSentences.map(s => s.sourceText).join('. ')}

Return ONLY a valid JSON object. Do not include markdown code blocks or any other text outside the JSON.
Follow this EXACT structure with exactly ${jsonExampleTasks.length} tasks:

${jsonExample}
        `.trim()

        try {
          const startTime = performance.now()
          const rawResponse = await streamOllamaGenerate({
            serverUrl,
            apiKey: process.env.REACT_APP_OLLAMA_KEY,
            body: {
              model: effectiveModel,
              prompt: taskPrompt,
              format: 'json',
              // Capped: 3 short tasks is ~400 tokens. Without this a vision model
              // can stream thousands of chars of repeating JSON and never stop.
              options: { temperature: 0.2, num_predict: 900 }
            },
            onProgress: (acc) => {
              setProgressMsg(`Generating homework tasks… (${acc.length} chars streamed)`)
            }
          })
          const elapsed = ((performance.now() - startTime) / 1000).toFixed(2)
          console.log(`[Timer] Task generation took ${elapsed} seconds`)
          const parsed = parseLooseJson(rawResponse)
          if (parsed?.tasks && Array.isArray(parsed.tasks)) {
            finalTasks = parsed.tasks.slice(0, 3) // strictly 3 tasks
          }
        } catch (e) {
          console.warn('Failed to generate tasks', e)
        }
      }

      const finalData = {
        sentences: uniqueSentences,
        words: uniqueWords,
        tasks: finalTasks
      }

      const totalElapsed = ((performance.now() - totalStartTime) / 1000).toFixed(2)
      console.log('--- AI Analysis Complete & Cleaned ---')
      console.log(`[Timer] Total Analysis Time: ${totalElapsed} seconds`)
      console.log('Parsed Sentences:', finalData.sentences)
      console.log('Parsed Words:', finalData.words)
      console.log('Parsed Tasks:', finalData.tasks)

      setPreviewData(finalData)
      setStatus('reviewing')
    } catch (err) {
      console.error(err)
      setStatus('error')
      if (err.message.includes('Failed to fetch') || err.message.includes('Load failed')) {
        setErrorMessage('Connection to Ollama failed. The tunnel may be down or the request may have been dropped mid-stream. Try a smaller model, or check that the Mac Studio is reachable.')
      } else {
        setErrorMessage(err.message || 'Failed to analyze documents. Make sure Ollama is running.')
      }
    }
  }

  const handleSave = async () => {
    if (!previewData) return
    setStatus('saving')

    try {
      await addMeetingWithContent({
        variables: {
          input: {
            meeting: {
              title: meetingTitle || 'New Meeting',
              type: meetingType || 'Language Class',
              description: `Meeting generated from documents`,
              meetingDate: meetingDate || null,
              username: username
            },
            sentences: previewData.sentences || [],
            words: previewData.words || [],
            tasks: previewData.tasks || []
          }
        }
      })
      setStatus('completed')
      setTimeout(() => {
        handleClose()
      }, 2000)
    } catch (err) {
      console.error(err)
      setStatus('error')
      setErrorMessage(err.message)
    }
  }

  if (!isOpen) return null

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
        <button className={styles.closeBtn} onClick={handleClose}>
          <CloseIcon />
        </button>

        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>AI Analysis</div>
            <h2 className={styles.title}>Meeting Documents</h2>
          </div>
        </div>

        <div className={styles.container}>
          <div style={{ display: 'flex', gap: '1.5rem' }}>
            <div className={styles.field} style={{ flex: 1 }}>
              <div className={styles.fieldLabel}>Meeting Title</div>
              <input
                type="text"
                className={styles.input}
                value={meetingTitle}
                onChange={(e) => setMeetingTitle(e.target.value)}
                placeholder="e.g. Portuguese Class"
              />
            </div>
            <div className={styles.field} style={{ flex: 1 }}>
              <div className={styles.fieldLabel}>Meeting Type</div>
              <select className={styles.input} value={meetingType} onChange={(e) => setMeetingType(e.target.value)}>
                <option value="Language Class">Language Class</option>
                <option value="Business Meeting">Business Meeting</option>
                <option value="Workshop">Workshop</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1.5rem' }}>
            <div className={styles.field} style={{ flex: 1 }}>
              <div className={styles.fieldLabel}>Meeting Date</div>
              <input
                type="date"
                className={styles.input}
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1.5rem' }}>
            <div className={styles.field} style={{ flex: 1 }}>
              <div className={styles.fieldLabel}>Target Language</div>
              <select className={styles.input} value={targetLanguage} onChange={(e) => setTargetLanguage(e.target.value)}>
                {Object.entries(languages).map(([key, value]) => (
                  <option key={key} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.field} style={{ flex: 1 }}>
              <div className={styles.fieldLabel}>Ollama Model</div>
              <select className={styles.input} value={model} onChange={(e) => setModel(e.target.value)}>
                {MODELS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.field}>
            <div className={styles.fieldLabel}>Documents (.png, .jpg, .txt, .xlsx)</div>
            <input type="file" multiple accept=".jpg,.jpeg,.png,.txt,.xlsx,.xls" onChange={handleFileChange} className={styles.input} style={{ cursor: 'pointer' }} />
            {files.length > 0 && (
              <div style={{ marginTop: '0.8rem', fontSize: '0.85rem', color: '#6ee7b7' }}>
                {files.length} file(s) selected: {files.map((f) => f.name).join(', ')}
              </div>
            )}
          </div>

          <div className={styles.field}>
            <button
              type="button"
              onClick={() => setShowPaste((s) => !s)}
              style={{
                background: 'transparent', border: '1px dashed #3a3a42', color: '#6ee7b7',
                borderRadius: '8px', padding: '0.6rem 0.9rem', cursor: 'pointer',
                fontSize: '0.85rem', width: '100%', textAlign: 'left'
              }}
            >
              {showPaste ? '▾' : '▸'} Paste lesson text directly {pastedText.trim() && !showPaste ? `(${cleanChatText(pastedText).split('\n').filter(Boolean).length} lines ready)` : ''}
            </button>

            {showPaste && (
              <>
                <textarea
                  value={pastedText}
                  onPaste={handlePaste}
                  onChange={(e) => setPastedText(e.target.value)}
                  placeholder={'Paste straight from your Preply / lesson chat (⌘V).\nImages are removed automatically and line breaks are kept.'}
                  spellCheck="false"
                  style={{
                    marginTop: '0.6rem', width: '100%', minHeight: '160px', resize: 'vertical',
                    background: '#0d0d0f', border: '1px solid #3a3a42', borderRadius: '8px',
                    color: '#e8e8ec', fontFamily: "'DM Mono', monospace", fontSize: '0.85rem',
                    lineHeight: 1.5, padding: '0.7rem', boxSizing: 'border-box', whiteSpace: 'pre-wrap'
                  }}
                />
                <div style={{ marginTop: '0.4rem', display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#94a3b8' }}>
                  <span>{cleanChatText(pastedText).split('\n').filter(Boolean).length} lines after cleanup (names, times & images stripped)</span>
                  {pastedText && <button type="button" onClick={() => setPastedText('')} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '0.75rem' }}>clear</button>}
                </div>
              </>
            )}
          </div>

          {status === 'analyzing' && (
            <div style={{ color: '#6ee7b7', fontSize: '0.9rem', marginTop: '1rem', fontStyle: 'italic', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              <div>Sending to Mac Studio Ollama for analysis...</div>
              {progressMsg && <div style={{ fontWeight: 'bold' }}>{progressMsg}</div>}
            </div>
          )}

          {status === 'error' && <div style={{ color: '#ff4d4d', fontSize: '0.9rem', marginTop: '1rem' }}>{errorMessage}</div>}

          {status === 'completed' && (
            <div style={{ color: '#6ee7b7', fontSize: '0.9rem', marginTop: '1rem' }}>Successfully saved meeting and all extracted data to the database!</div>
          )}

          {status === 'reviewing' && previewData && (
            <div
              style={{
                marginTop: '1rem',
                padding: '1rem',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '12px',
                maxHeight: '300px',
                overflowY: 'auto',
                border: '1px solid #3a3a42'
              }}>
              <h4 style={{ margin: '0 0 1rem 0', color: '#6ee7b7' }}>Preview Extracted Data</h4>

              <div style={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>Sentences ({previewData.sentences?.length || 0})</div>
              {previewData.sentences?.map((s, i) => (
                <div key={i} style={{ fontSize: '0.85rem', marginBottom: '0.3rem' }}>
                  <span style={{ color: '#6ee7b7' }}>{s.sourceText}</span> → {s.targetText}
                </div>
              ))}

              <div style={{ marginBottom: '0.5rem', marginTop: '1rem', fontWeight: 'bold' }}>Words ({previewData.words?.length || 0})</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {previewData.words?.map((w, i) => (
                  <span key={i} style={{ fontSize: '0.8rem', background: '#222226', padding: '0.2rem 0.5rem', borderRadius: '4px' }}>
                    {w.word} = {w.translation}
                  </span>
                ))}
              </div>

              <div style={{ marginBottom: '0.5rem', marginTop: '1rem', fontWeight: 'bold' }}>Tasks ({previewData.tasks?.length || 0})</div>
              {previewData.tasks?.map((t, i) => (
                <div key={i} style={{ fontSize: '0.85rem', marginBottom: '0.3rem' }}>
                  <strong style={{ color: '#6ee7b7' }}>{t.title}</strong> ({t.type}): {t.description}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          {status !== 'reviewing' && status !== 'saving' && status !== 'completed' && (
            <button className={styles.primaryBtn} onClick={handleAnalyze} disabled={(files.length === 0 && !pastedText.trim()) || status === 'analyzing'}>
              {status === 'analyzing' ? 'Analyzing...' : 'Analyze Documents'}
            </button>
          )}
          {status === 'reviewing' && (
            <button className={styles.primaryBtn} onClick={handleSave}>
              Save Meeting
            </button>
          )}
          {status === 'saving' && (
            <button className={styles.primaryBtn} disabled>
              Saving...
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
